import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import { MediaIdField, useMedia } from '@/features/media';

import { fromAppearance, toAppearancePatch } from '../api/mappers';
import { useChatSettingsForm, type ChatSettingsTabProps } from '../hooks/use-chat-settings-form';
import { appearanceSchema, type AppearanceForm } from '../schemas/appearance-schema';

import { FormAlert, SaveButton, SwitchField, TextField } from './form-controls';
import { WidgetPreview } from './widget-preview';

const GROUPS = ['enabled', 'appearance'] as const;

function ChoiceField({
  value,
  onChange,
  options,
  disabled,
  name,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  disabled: boolean;
  name: string;
}) {
  return (
    <RadioGroup
      value={value}
      onValueChange={onChange}
      disabled={disabled}
      className="flex flex-wrap gap-4"
    >
      {options.map((option) => (
        <div key={option.value} className="flex items-center gap-2">
          <RadioGroupItem value={option.value} id={`${name}-${option.value}`} />
          <Label htmlFor={`${name}-${option.value}`} className="font-normal">
            {option.label}
          </Label>
        </div>
      ))}
    </RadioGroup>
  );
}

/** Widget on/off, launcher shape and side, colour, header and greetings, with a live preview. */
export function AppearanceTab({ storeId, settings, readOnly }: ChatSettingsTabProps) {
  const { t } = useTranslation('chatSettings');
  const { form, submit, saving } = useChatSettingsForm<AppearanceForm>({
    storeId,
    schema: appearanceSchema,
    defaultValues: fromAppearance(settings),
    toPatch: toAppearancePatch,
    groups: GROUPS,
    readOnly,
  });
  const appearance = useWatch({ control: form.control, name: 'appearance' });
  const logo = useMedia(appearance.logoMediaId);
  const grabber = useMedia(appearance.attentionGrabberMediaId);
  const side = (value: 'left' | 'right') => t(`fields.side.${value}`);
  const sides = [
    { value: 'right', label: side('right') },
    { value: 'left', label: side('left') },
  ];

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate>
        <FormLayout
          asideWidth="wide"
          aside={
            <FormSection title={t('appearance.previewTitle')} className="xl:sticky xl:top-20">
              <WidgetPreview
                appearance={appearance}
                logoUrl={appearance.logoMediaId ? (logo.data?.url ?? null) : null}
                attentionGrabberUrl={
                  appearance.attentionGrabberMediaId ? (grabber.data?.url ?? null) : null
                }
                allowUploads={settings.behaviour.allowUploads}
              />
            </FormSection>
          }
        >
          <FormAlert message={form.formState.errors.root?.server?.message} />
          <FormSection title={t('appearance.widgetTitle')}>
            <SwitchField
              control={form.control}
              name="enabled"
              label={t('fields.enabled')}
              description={t('fields.enabledHint')}
              disabled={readOnly}
            />
          </FormSection>
          <FormSection title={t('appearance.launcherTitle')}>
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="appearance.shape"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.shapeLabel')}</FormLabel>
                    <ChoiceField
                      name="shape"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={readOnly}
                      options={[
                        { value: 'round', label: t('fields.shape.round') },
                        { value: 'pill', label: t('fields.shape.pill') },
                      ]}
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="appearance.position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.position')}</FormLabel>
                    <ChoiceField
                      name="position"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={readOnly}
                      options={sides}
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="appearance.mobilePosition"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.mobilePosition')}</FormLabel>
                    <ChoiceField
                      name="mobilePosition"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={readOnly}
                      options={sides}
                    />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="appearance.primaryColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.primaryColor')}</FormLabel>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="color"
                      aria-label={t('fields.primaryColorPicker')}
                      className="border-input h-9 w-12 cursor-pointer rounded-md border bg-transparent p-1 disabled:cursor-not-allowed"
                      value={/^#[0-9a-f]{6}$/i.test(field.value) ? field.value : '#000000'}
                      disabled={readOnly}
                      onChange={(event) => field.onChange(event.target.value)}
                    />
                    <FormControl>
                      <Input
                        className="w-32 font-mono"
                        placeholder="#1f2937"
                        disabled={readOnly}
                        {...field}
                      />
                    </FormControl>
                    {field.value ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={readOnly}
                        onClick={() => field.onChange('')}
                      >
                        {t('fields.primaryColorReset')}
                      </Button>
                    ) : null}
                  </div>
                  <FormDescription>{t('fields.primaryColorHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <FormSection title={t('appearance.headerTitle')}>
            <TextField
              control={form.control}
              name="appearance.headerTitle"
              label={t('fields.headerTitle')}
              placeholder={t('preview.defaultTitle')}
              description={t('fields.headerTitleHint')}
              disabled={readOnly}
            />
            <TextField
              control={form.control}
              name="appearance.greetingOnline"
              label={t('fields.greetingOnline')}
              multiline
              disabled={readOnly}
            />
            <TextField
              control={form.control}
              name="appearance.greetingAway"
              label={t('fields.greetingAway')}
              multiline
              disabled={readOnly}
            />
            <TextField
              control={form.control}
              name="appearance.greetingOffline"
              label={t('fields.greetingOffline')}
              multiline
              disabled={readOnly}
            />
            <SwitchField
              control={form.control}
              name="appearance.showAgentAvatars"
              label={t('fields.showAgentAvatars')}
              description={t('fields.showAgentAvatarsHint')}
              disabled={readOnly}
            />
          </FormSection>
          <FormSection title={t('appearance.imagesTitle')}>
            <FormField
              control={form.control}
              name="appearance.logoMediaId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.logo')}</FormLabel>
                  <MediaIdField
                    value={field.value}
                    onChange={field.onChange}
                    label={t('fields.logo')}
                    disabled={readOnly}
                  />
                  <FormDescription>{t('fields.logoHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="appearance.attentionGrabberMediaId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.attentionGrabber')}</FormLabel>
                  <MediaIdField
                    value={field.value}
                    onChange={field.onChange}
                    label={t('fields.attentionGrabber')}
                    disabled={readOnly}
                  />
                  <FormDescription>{t('fields.attentionGrabberHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <SaveButton saving={saving} dirty={form.formState.isDirty} hidden={readOnly} />
        </FormLayout>
      </form>
    </Form>
  );
}
